import { useState } from "react";
import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";
import { useOpsObligations } from "@/components/ops/useOpsObligations";
import { OpsObligationEditor } from "@/components/ops/OpsObligationEditor";
import {
  OPS_OBLIGATION_KIND_LABELS,
  OPS_OBLIGATION_KINDS,
  OPS_OBLIGATION_STATUSES,
  type OpsObligationKind,
} from "@/lib/opsObligations/opsObligationFields";
import type { OpsObligation } from "@/lib/opsObligationsClient";

const selectClass =
  "rounded border border-gray-300 px-2 py-1.5 text-sm dark:border-zinc-600 dark:bg-zinc-900";
const inputClass =
  "rounded border border-gray-300 px-2 py-1.5 text-sm dark:border-zinc-600 dark:bg-zinc-900";

function locationLabel(
  row: OpsObligation,
  locations: Array<{ id: string; name: string }>,
): string {
  if (!row.location_id) return "Organization";
  return locations.find((l) => l.id === row.location_id)?.name ?? "Location";
}

/**
 * Ops → Obligations (OB-1…OB-5): utilities, internet, leases, mortgages.
 * See docs/planning/ops_obligations_mode.md.
 */
export function OpsObligationsPanel() {
  const organizationId = useScopedOrganizationId();
  const vm = useOpsObligations(organizationId);
  const [newKind, setNewKind] = useState<OpsObligationKind>("utility");
  const [newProvider, setNewProvider] = useState("");

  if (!organizationId) {
    return (
      <div className="w-full text-left space-y-3">
        <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">Obligations</h2>
        <p className="text-sm text-gray-500 dark:text-zinc-400">Select a workspace organization first.</p>
      </div>
    );
  }

  return (
    <div data-app-panel="OpsObligations" className="w-full text-left space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">Obligations</h2>
          <p className="mt-1 max-w-3xl text-sm text-gray-600 dark:text-zinc-300">
            Recurring bills, utilities, internet, leases, and mortgages for the organization or a
            location.
          </p>
        </div>
        <button
          type="button"
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm dark:border-zinc-600"
          onClick={() => void vm.reload()}
          disabled={vm.loading}
        >
          {vm.loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {vm.error ? (
        <p className="text-sm text-red-700 dark:text-red-300" role="alert">
          {vm.error}
        </p>
      ) : null}

      <div className="flex flex-wrap items-end gap-2">
        <label className="text-sm text-gray-700 dark:text-zinc-300">
          Kind
          <select className={`mt-1 block ${selectClass}`} value={vm.kind} onChange={(e) => vm.setKind(e.target.value)}>
            <option value="">All</option>
            {OPS_OBLIGATION_KINDS.map((k) => (
              <option key={k} value={k}>
                {OPS_OBLIGATION_KIND_LABELS[k]}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm text-gray-700 dark:text-zinc-300">
          Status
          <select
            className={`mt-1 block ${selectClass}`}
            value={vm.status}
            onChange={(e) => vm.setStatus(e.target.value)}
          >
            <option value="">All</option>
            {OPS_OBLIGATION_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm text-gray-700 dark:text-zinc-300">
          Location
          <select
            className={`mt-1 block ${selectClass}`}
            value={vm.locationId}
            onChange={(e) => vm.setLocationId(e.target.value)}
          >
            <option value="">All</option>
            <option value="org">Organization-wide</option>
            {vm.locations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <label className="text-sm text-gray-700 dark:text-zinc-300">
          New kind
          <select
            className={`mt-1 block ${selectClass}`}
            value={newKind}
            onChange={(e) => setNewKind(e.target.value as OpsObligationKind)}
          >
            {OPS_OBLIGATION_KINDS.map((k) => (
              <option key={k} value={k}>
                {OPS_OBLIGATION_KIND_LABELS[k]}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm text-gray-700 dark:text-zinc-300">
          Provider
          <input
            className={`mt-1 block w-56 ${inputClass}`}
            value={newProvider}
            onChange={(e) => setNewProvider(e.target.value)}
            placeholder="Duke Energy"
          />
        </label>
        <button
          type="button"
          className="rounded-md bg-slate-800 px-3 py-1.5 text-sm text-white dark:bg-zinc-200 dark:text-zinc-900"
          onClick={() => {
            const name = newProvider.trim();
            if (!name) return;
            void vm.create({ kind: newKind, provider_name: name, status: "draft" }).then(() => setNewProvider(""));
          }}
        >
          Add
        </button>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(16rem,22rem)_1fr]">
        <ul className="divide-y divide-gray-200 rounded border border-gray-200 dark:divide-zinc-700 dark:border-zinc-700">
          {vm.rows.length === 0 ? (
            <li className="px-3 py-4 text-sm text-gray-500 dark:text-zinc-400">No obligations yet.</li>
          ) : (
            vm.rows.map((row) => {
              const active = row.id === vm.selectedId;
              return (
                <li key={row.id}>
                  <button
                    type="button"
                    data-testid="ops-obligation-row"
                    className={`w-full px-3 py-2 text-left text-sm ${
                      active
                        ? "bg-slate-100 dark:bg-zinc-800"
                        : "hover:bg-gray-50 dark:hover:bg-zinc-900"
                    }`}
                    onClick={() => vm.setSelectedId(row.id)}
                  >
                    <div className="font-medium text-gray-900 dark:text-zinc-100">
                      {row.provider_name || "(unnamed)"}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-zinc-400">
                      {OPS_OBLIGATION_KIND_LABELS[row.kind]} · {locationLabel(row, vm.locations)} · {row.status}
                    </div>
                  </button>
                </li>
              );
            })
          )}
        </ul>
        {vm.detail ? (
          <OpsObligationEditor
            key={vm.detail.id}
            obligation={vm.detail}
            locations={vm.locations}
            attachments={vm.attachments}
            onSave={(patch) => vm.save(vm.detail!.id, patch)}
            onDelete={() => vm.remove(vm.detail!.id)}
            onAddFile={(file) => vm.addAttachment(file)}
            onRemoveAttachment={(id) => vm.removeAttachment(id)}
          />
        ) : (
          <p className="text-sm text-gray-500 dark:text-zinc-400">Select a row or add one to edit.</p>
        )}
      </div>
    </div>
  );
}
